import styled, { css } from 'styled-components';

// CONSTANTS
import { theme } from '../../constants/theme';
import { responsiveEndPoints } from '../../constants/responsive-endpoints';

// ENUMS
import { AlertCardType, AlertSize, AlertTextType, AlertType } from '../../enums/alert';

export const Label = styled.label<{ cardType: AlertCardType; hasBadge: boolean; size: AlertSize }>`
  display: block;
  margin-bottom: ${({ hasBadge }) => (hasBadge ? '0' : '4px')};
  font-family: 'Clan Pro';
  font-weight: ${theme.weights.semibold};
  letter-spacing: 0.02em;

  ${({ size }) =>
    size === AlertSize.LARGE &&
    css`
      margin-bottom: 8px;
      letter-spacing: 0.02em;
      line-height: 22px;
      color: ${theme.colors.primary.black};
    `}

  ${({ cardType }) =>
    cardType === AlertCardType.SPACE &&
    css`
      &:first-child {
        font-size: 12px;
        line-height: 16px !important;
      }
    `}

  > i {
    margin-left: 4px;
    font-style: normal;
    text-decoration: underline;
  }
`;

export const Container = styled.div<{ hasBadge: boolean; size: AlertSize; type: AlertType }>`
  display: flex;
  position: relative;
  margin-bottom: 32px;
  padding: 16px 33px 16px 24px;
  border-radius: 8px;
  box-sizing: border-box;
  font-family: 'Clan Pro';
  font-weight: ${theme.weights.regular};

  > svg {
    left: 20px;
    position: absolute;
  }

  ${({ size }) =>
    size === AlertSize.LARGE &&
    css`
      display: flex;
      align-items: center;
      padding: 20px 18px;
      color: ${theme.colors.secondary.gray200};
      font-size: 13px;

      svg {
        position: static;
        margin-right: 18px;
      }
    `}

  ${({ type }) =>
    type === AlertType.LIGHT &&
    css`
      color: ${theme.colors.primary.black};
      background: ${theme.colors.primary.white};
      border: 1px solid ${theme.colors.primary.transparentBorder};
    `}

  ${({ type }) =>
    type === AlertType.PRIMARY &&
    css`
      background: ${theme.colors.secondary.blue600};
      border: 1px solid ${theme.colors.secondary.blue500};
      color: ${theme.colors.secondary.gray200};
      font-size: 12.6px;
      line-height: 24px;

      ${Label} {
        color: ${theme.colors.primary.black};
      }

      @media (max-width: 1270px) {
        width: 95%;
      }
    `}

  ${({ type }) =>
    type === AlertType.WAITING &&
    css`
      background: ${theme.colors.secondary.blue600};
      border: 1px solid ${theme.colors.secondary.blue500};
    `}

  ${({ type }) =>
    type === AlertType.PROCESS &&
    css`
      background: ${theme.colors.secondary.green600};
      border: 1px solid ${theme.colors.secondary.green500};
      svg {
        path:first-child {
          fill: ${theme.colors.secondary.green200};
        }
      }
    `}
  
  ${({ type }) =>
    (type === AlertType.WAIVER || type === AlertType.EXPIRED) &&
    css`
      background: ${theme.colors.secondary.lilac600};
      border: 1px solid ${theme.colors.secondary.lilac500};
    `}
  
  ${({ type }) =>
    type === AlertType.PENDING &&
    css`
      background: ${theme.colors.secondary.orange600};
      border: 1px solid ${theme.colors.secondary.orange500};
      flex-wrap: wrap;
      svg {
        rect:first-child {
          stroke: ${theme.colors.secondary.orange200};
        }
        rect:nth-child(2) {
          fill: ${theme.colors.secondary.orange200};
        }
        rect:nth-child(3) {
          fill: ${theme.colors.secondary.orange200};
        }
      }
    `}

  ${({ hasBadge }) =>
    hasBadge &&
    css`
      padding: 17px 40px 17px 10px;
      justify-content: space-between;
    `}
  
  ${({ type }) =>
    type === AlertType.DEFAULT &&
    css`
      background: ${theme.colors.secondary.blue600};
      border: 1px solid ${theme.colors.primary.white};
      box-shadow: 0 0 0 1px hsl(204deg 96% 81%);
      margin: 0 1px;

      svg {
        path:first-child {
          fill: ${theme.colors.primary.bariBlue};
        }
      }
    `}
  
  ${({ type }) =>
    type === AlertType.APPROVED &&
    css`
      background: ${theme.colors.secondary.green600};
      border: 1px solid ${theme.colors.secondary.green400};
    `}

  ${({ type }) =>
    type === AlertType.DENIED &&
    css`
      background: ${theme.colors.secondary.red600};
      border: 1px solid ${theme.colors.secondary.red500};
    `}

  ${({ type }) =>
    type === AlertType.NOT_SENT &&
    css`
      background: ${theme.colors.secondary.yellow600};
      border: 1px soli ${theme.colors.secondary.yellow400};
    `}

    @media (max-width: ${responsiveEndPoints.responsive}px) {
      flex-direction: row;
      padding: 16px 23px 16px 24px;
    }
    @media (max-width: ${responsiveEndPoints.mobile}px) {
      flex-direction: row;
      flex-wrap: unset;
      padding: 16px 23px 16px 24px;
    } 

`;

const defaultSectionStyled = [AlertCardType.DEFAULT, AlertCardType.SPACE];

export const Section = styled.section<{ cardType: AlertCardType }>`
  display: ${({ cardType }) => defaultSectionStyled.includes(cardType) && 'flex'};
  padding-left: ${({ cardType }) => defaultSectionStyled.includes(cardType) && '10px'};

  ${({ cardType }) =>
    cardType === AlertCardType.DEFAULT &&
    css`
      flex-direction: column;
    `}

  ${({ cardType }) =>
    cardType === AlertCardType.SPACE &&
    css`
      justify-content: space-between;
      align-items: center;
      width: 100%;
    `}

    @media (max-width: ${responsiveEndPoints.mobile}px) {
      line-height: 24px;
    }    
`;

export const Span = styled.span<{ size: AlertSize; textType: AlertTextType; cardType: AlertCardType; type: AlertType }>`
  ${({ textType }) =>
    textType === AlertTextType.ALERT &&
    css`
      display: flex;
      flex-direction: column;
    `}

  ${({ textType }) =>
    textType === AlertTextType.ALERT_WRAP &&
    css`
      display: flex;
      flex-wrap: wrap;
    `}

  ${({ size }) =>
    size === AlertSize.LARGE &&
    css`
      display: flex;
      font-family: 'Clan Pro';
      font-weight: ${theme.weights.regular};
      color: ${theme.colors.secondary.gray200};
      flex-wrap: wrap;
    `}

  ${({ cardType, size, type }) =>
    cardType === AlertCardType.SPACE &&
    size === AlertSize.LARGE &&
    type === AlertType.LIGHT &&
    css`
      display: flex;
      justify-content: space-between;
      width: 100%;
      > div > a {
      }
    `}
`;

export const Link = styled.a<{ isButtonBlock: boolean; cardType: AlertCardType; size: AlertSize; type: AlertType }>`
  font-family: 'Clan Pro';
  font-weight: ${theme.weights.semibold};
  color: ${theme.colors.primary.bariBlue};
  cursor: pointer;

  ${({ isButtonBlock }) =>
    isButtonBlock &&
    css`
      display: block;
    `}

  padding-left: 5px;
  @media (max-width: ${responsiveEndPoints.mobile}px) {
    padding-left: 0px;
    padding-top: 5px;
  }
`;
